import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Microscope, FileCheck2, ShieldCheck, Quote, Star, FlaskConical, Newspaper, ArrowUpRight,
  ArrowDown, ArrowRight, Search, Syringe, Dumbbell, Pill, Activity, Printer, PackageCheck,
  Building2, AlertTriangle, BadgeDollarSign, CheckCircle2, XCircle, Lock,
  Mail, Send, MessageSquare, Loader2, Beaker,
} from "lucide-react";
import { Section, SectionHeading, Eyebrow, PillButton, CheckItem } from "@/components/Section";
import { TIERS, TESTIMONIALS, FEATURED_QUOTE, LAB_CAPABILITIES } from "@/data/site";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const INPUT_CLS = "w-full rounded-lg border border-border bg-white px-4 py-3 text-sm text-navy outline-none transition-colors placeholder:text-navy/40 focus:border-crimson";

function InquiryForm({ type, cta = "Send message" }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/inquiries", { type, ...form });
      toast.success("Thank you — our team will be in touch shortly.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-3.5" data-testid={`inquiry-form-${type}`}>
      <div className="grid gap-3.5 sm:grid-cols-2">
        <input required placeholder="Your name" value={form.name} onChange={set("name")} data-testid={`${type}-name`} className={INPUT_CLS} />
        <input required type="email" placeholder="Email address" value={form.email} onChange={set("email")} data-testid={`${type}-email`} className={INPUT_CLS} />
      </div>
      {type === "b2b" && (
        <input placeholder="Company / brand" value={form.company} onChange={set("company")} data-testid="b2b-company" className={INPUT_CLS} />
      )}
      <textarea required rows={4} value={form.message} onChange={set("message")} data-testid={`${type}-message`} className={INPUT_CLS}
        placeholder={type === "b2b" ? "Tell us about your product line — how many SKUs, which categories, and what you need verified." : "What would you like to ask our science team?"} />
      <button type="submit" disabled={loading} data-testid={`${type}-submit`}
        className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-crimson px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:bg-crimson-dark hover:-translate-y-0.5 active:scale-[0.99] disabled:opacity-60 sm:w-auto">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{cta} <Send className="h-4 w-4" /></>}
      </button>
    </form>
  );
}

// --- Real, reputable news articles about fake / adulterated / mislabeled supplements ---
const NEWS = [
  { source: "Harvard Health", title: "Watch out for bogus supplement claims", href: "https://www.health.harvard.edu/healthy-aging-and-longevity/watch-out-for-bogus-supplement-claims" },
  { source: "University of Mississippi", title: "Nearly 90% of sports supplements were inaccurately labeled, study finds", href: "https://olemiss.edu/news/2023/08/sports-supplements-labeling-study/" },
  { source: "U.S. FDA", title: "FDA warns consumers not to use counterfeit Ozempic (semaglutide)", href: "https://www.fda.gov/drugs/drug-alerts-and-statements/fda-warns-consumers-not-use-counterfeit-ozempic-semaglutide-found-us-drug-supply-chain" },
  { source: "AARP", title: "How to protect yourself from dangerous supplement scams", href: "https://www.aarp.org/money/scams-fraud/supplements/" },
  { source: "Consumer Reports", title: "Many protein powders contain concerning levels of lead, testing finds", href: "https://www.consumerreports.org/lead/protein-powders-and-shakes-contain-high-levels-of-lead-a4206364640/" },
  { source: "CBS News", title: "Herbal supplements targeted by New York attorney general over mislabeling", href: "https://www.cbsnews.com/news/herbal-supplements-targeted-by-new-york-attorney-general/" },
  { source: "U.S. FDA", title: "Male-enhancement & \u2018testosterone\u2019 products found spiked with hidden drugs", href: "https://www.fda.gov/drugs/medication-health-fraud-notifications/sexual-enhancement-and-energy-product-notifications" },
  { source: "Eurojust (EU)", title: "Takedown of criminal group selling fake medicines & supplements to seriously ill victims", href: "https://www.eurojust.europa.eu/news/takedown-criminal-group-selling-fake-medicines-and-supplements-mislead-seriously-ill-victims" },
  { source: "The Philadelphia Inquirer", title: "Counterfeit Ozempic and Wegovy drugs surface in the U.S. supply", href: "https://www.inquirer.com/health/counterfeit-ozempic-wegovy-drugs-pennsylvania-20250602.html" },
  { source: "VnExpress", title: "Hundreds of tons of fake dietary supplements produced at a Hanoi industrial park over a decade", href: "https://e.vnexpress.net/news/news/hundreds-of-tons-of-fake-dietary-supplements-produced-at-hanoi-industrial-park-over-decade-5007505.html" },
  { source: "U.S. FDA", title: "Weight-loss supplements found laced with undeclared prescription drugs", href: "https://www.fda.gov/drugs/medication-health-fraud-notifications/weight-loss-product-notifications" },
  { source: "Food & Wine", title: "Investigation finds heavy metals in popular protein powders", href: "https://www.foodandwine.com/protein-powder-heavy-metals-contamination-consumer-reports-investigation-11828759" },
  { source: "U.S. FDA", title: "Supplements substituted with toxic yellow oleander \u2014 safety alert", href: "https://www.fda.gov/food/alerts-advisories-safety-information/fda-issues-warning-about-certain-supplements-substituted-toxic-yellow-oleander" },
];

// --- ACTUAL FDA-flagged products & official FDA fraud warnings ---
const FDA_URL = "https://www.fda.gov/food/alerts-advisories-safety-information/fda-issues-warning-about-certain-supplements-substituted-toxic-yellow-oleander";
const FDA_FRAUD_URL = "https://www.fda.gov/consumers/consumer-updates/how-spot-health-fraud";
const OLEANDER = "FDA: substituted with toxic yellow oleander";
const FDA_PRODUCTS = [
  { name: "FDA: How to spot health fraud", img: "/fda-miracle-cure.png", note: "Official FDA consumer warning", href: FDA_FRAUD_URL, contain: true },
  { name: "Chupa Panza", img: "https://www.fda.gov/files/Chupa%20Panza.png", note: OLEANDER },
  { name: "SdB ELITE", img: "https://www.fda.gov/files/SdB%20ELITE.jpg", note: OLEANDER },
  { name: "New Me", img: "https://www.fda.gov/files/NewMe_0.JPG", note: OLEANDER },
  { name: "VidaSlim", img: "https://www.fda.gov/files/VidaSlim.JPG", note: OLEANDER },
  { name: "Green ELV Nutrition Herb Control", img: "https://www.fda.gov/files/Green%20ELV%20Nutrition%20Herb%20Control.png", note: OLEANDER },
  { name: "Primor Health Optimus Weight", img: "https://www.fda.gov/files/Primor-Health-Optimus-Weight-Creativa-Interiors-Privit-Wellness-Amazon_0.JPG", note: OLEANDER },
  { name: "ALIPOTEC Mexican Tejocote Root", img: "https://www.fda.gov/files/styles/square_1_1_225_x_225/public/Alipotec.png?itok=MkmI23dD", note: OLEANDER },
];

const CATS = [
  { id: "glp1", label: "GLP-1 / Weight-loss", icon: Syringe },
  { id: "testosterone", label: "Testosterone Booster", icon: Dumbbell },
  { id: "vitamin", label: "Vitamins & Minerals", icon: Pill },
  { id: "peptide", label: "Peptides", icon: Activity },
];

const OTHER_LIST = ["Pre-workout", "Fat burner", "Nootropic", "Probiotic", "Omega-3 / Fish oil", "Creatine", "Collagen", "Greens powder", "Sleep aid / Melatonin", "Ashwagandha", "Magnesium", "Protein powder", "Sexual health / ED"];

const CATEGORY_STATS = {
  glp1: "The FDA has warned that counterfeit and adulterated GLP-1 / weight-loss products are circulating — some spiked with undeclared drugs, and certain \u201cweight-loss\u201d supplements were found substituted with toxic yellow oleander, which can be fatal. (FDA, 2024\u20132026)",
  testosterone: "An analysis found nearly 90% of sports supplements were inaccurately labeled, and the FDA has repeatedly found \u201ctestosterone\u201d and male-enhancement products spiked with hidden, undeclared drugs. (Univ. of Mississippi; FDA)",
  vitamin: "Independent testing has repeatedly found vitamins and supplements containing far more \u2014 or far less \u2014 than the amount printed on the label.",
  peptide: "Counterfeit peptide and semaglutide products have entered the U.S. supply chain; the FDA warns they may contain unknown or unsafe substances. (FDA)",
  default: "The supplement industry is lightly regulated \u2014 studies and FDA testing routinely find products that don\u2019t match their labels or are adulterated with undeclared ingredients.",
};

const NEXT_STEPS = [
  { icon: Printer, t: "We generate your label", d: "Right after checkout, a prepaid shipping label is ready to print." },
  { icon: PackageCheck, t: "You stick it on & ship", d: "Tape it to your package and drop it at any USPS location." },
  { icon: Building2, t: "We receive your sample", d: "Your supplement arrives at our lab \u2014 logged and tracked." },
  { icon: FlaskConical, t: "We do the science", d: "We analyze it and deliver your clear PDF report \u2014 7-day turnaround." },
  { icon: BadgeDollarSign, t: "If it\u2019s fake, you may be rewarded", d: "If results show a product is adulterated or fraudulent, you could be eligible for a whistleblower reward \u2014 your report is the evidence." },
];

const JOURNEY = [
  { img: "/journey/stick.png", label: "Stick the label on" },
  { img: "/journey/ship.png", label: "Mail it to our lab" },
  { img: "/journey/science.png", label: "We run the science" },
  { img: "/journey/report.png", label: "Get your report" },
];

function NewsCard({ source, title, href }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid="hero-news-card"
      className="block rounded-3xl border border-border bg-white p-5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_18px_50px_rgba(47,76,120,0.12)]">
      <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-periwinkle-dark">
        <Newspaper className="h-3.5 w-3.5" /> {source}
      </span>
      <p className="mt-2 font-heading text-[15px] font-semibold leading-snug text-navy">{title}</p>
      <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-navy/70">Read the story <ArrowUpRight className="h-3.5 w-3.5" /></span>
    </a>
  );
}

function FdaTile({ name, img, note, href, contain }) {
  return (
    <a href={href || FDA_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-fda-tile"
      className="block overflow-hidden rounded-3xl border border-border bg-white transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_18px_50px_rgba(47,76,120,0.12)]">
      <div className="relative">
        <img src={img} alt={`${name} — FDA-flagged`} className={`h-44 w-full bg-white ${contain ? "object-contain" : "object-cover"}`} loading="lazy" />
        <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-red-600 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white">
          <AlertTriangle className="h-3 w-3" /> FDA Warning
        </span>
      </div>
      <div className="p-3">
        <p className="line-clamp-2 text-xs font-semibold text-navy">{name}</p>
        <p className="mt-0.5 text-[11px] text-navy/50">{note || "Substituted with toxic yellow oleander"}</p>
      </div>
    </a>
  );
}

function DashboardPreview() {
  const rows = [
    { n: "Test Booster X", s: "FAIL", ok: false },
    { n: "Daily Multivitamin", s: "PASS", ok: true },
    { n: "GLP-1 Drops", s: "PASS", ok: true },
  ];
  return (
    <div className="rounded-2xl border border-border bg-white p-4 shadow-[0_10px_30px_rgba(47,76,120,0.08)]">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wide text-navy/70">Your Results</span>
        <FlaskConical className="h-4 w-4 text-periwinkle-dark" />
      </div>
      <div className="mt-3 space-y-2">
        {rows.map((r) => (
          <div key={r.n} className="flex items-center justify-between rounded-xl bg-cream px-3 py-2">
            <span className="text-xs font-medium text-navy">{r.n}</span>
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${r.ok ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
              {r.ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />} {r.s}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CategorySelector({ selected, setSelected }) {
  const navigate = useNavigate();
  const [tier, setTier] = useState("full");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const choose = (label, statKey) => { setSelected({ label, statKey }); setOpen(false); setQ(""); };
  const startCheckout = () => navigate(`/checkout?tier=${tier}&name=${encodeURIComponent(selected.label)}`);
  const filtered = OTHER_LIST.filter((o) => o.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="rounded-[1.75rem] border border-border bg-white p-6 shadow-[0_22px_60px_rgba(47,76,120,0.1)]" data-testid="hero-selector">
      {/* Step 1 — category picker (always visible) */}
      <p className="font-heading text-lg font-semibold text-navy">What do you want to test?</p>
      <p className="mt-1 text-sm text-navy/55">Pick a category — we&rsquo;ll show you the risks and your options right below.</p>
      <div className="mt-4 grid grid-cols-2 gap-3">
        {CATS.map((c) => {
          const active = selected?.label === c.label;
          return (
            <button key={c.id} onClick={() => active ? setSelected(null) : choose(c.label, c.id)} data-testid={`hero-category-${c.id}`}
              className={`group flex items-center gap-3 rounded-2xl border px-4 py-3.5 text-left transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.98] ${active ? "border-navy bg-navy" : "border-border bg-cream hover:border-navy hover:bg-navy"}`}>
              <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg transition-colors duration-200 ${active ? "bg-crimson" : "bg-periwinkle group-hover:bg-crimson"}`}>
                <c.icon className={`h-5 w-5 transition-colors duration-200 ${active ? "text-white" : "text-navy group-hover:text-white"}`} />
              </span>
              <span className={`text-sm font-semibold transition-colors duration-200 ${active ? "text-white" : "text-navy group-hover:text-white"}`}>{c.label}</span>
            </button>
          );
        })}
      </div>
      <div className="relative mt-3">
        {open && <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />}
        <div className="relative z-30">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-navy/40" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
            placeholder="Other — type your supplement…" data-testid="hero-other-input"
            className={`w-full rounded-2xl border bg-cream py-3.5 pl-11 pr-4 text-sm font-medium text-navy outline-none transition-colors focus:border-navy ${selected && selected.statKey === "default" ? "border-navy" : "border-border"}`} />
          {open && (
            <div className="absolute left-0 right-0 z-30 mt-2 max-h-60 overflow-y-auto rounded-2xl border border-border bg-white p-2 shadow-[0_20px_60px_rgba(47,76,120,0.18)]" data-testid="hero-other-dropdown">
              {filtered.map((o, i) => (
                <button key={o} onClick={() => choose(o, "default")} data-testid={`hero-other-option-${i}`}
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-navy transition-colors hover:bg-secondary">
                  <Pill className="h-4 w-4 text-navy/40" /> {o}
                </button>
              ))}
              {q.trim() && (
                <button onClick={() => choose(q.trim(), "default")}
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-navy hover:bg-secondary">
                  <Search className="h-4 w-4 text-navy/40" /> Test &ldquo;{q.trim()}&rdquo;
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Step 2 — risk + plan picker (appears below, selector stays put) */}
      {selected && (
        <div className="mt-5 border-t border-border pt-5" data-testid="hero-offer">
          <div className="flex gap-3 rounded-2xl border border-red-200 bg-red-50/70 p-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
            <p className="text-sm leading-relaxed text-red-900/80">{CATEGORY_STATS[selected.statKey] || CATEGORY_STATS.default}</p>
          </div>

          <p className="mt-4 text-sm font-semibold text-navy">Choose your test — tap to compare. Free shipping included:</p>
          <div className="mt-2 grid gap-3">
            {TIERS.map((t) => {
              const active = tier === t.id;
              return (
                <button key={t.id} onClick={() => setTier(t.id)} data-testid={`hero-plan-${t.id}`}
                  className={`relative rounded-xl border bg-cream px-5 py-4 text-left transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.99] ${active ? "border-crimson ring-2 ring-crimson/30" : "border-border"}`}>
                  {t.popular && <span className="absolute -top-2.5 right-4 rounded-md bg-crimson px-2.5 py-0.5 text-[10px] font-bold text-white">Most chosen</span>}
                  <span className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <span className={`grid h-5 w-5 place-items-center rounded-full border ${active ? "border-crimson bg-crimson" : "border-navy/30"}`}>
                        {active && <CheckCircle2 className="h-4 w-4 text-white" />}
                      </span>
                      <span className="text-sm font-bold text-navy">{t.name}</span>
                    </span>
                    <span className="font-heading text-xl font-bold text-navy">${t.price}</span>
                  </span>
                  <span className="mt-1.5 block text-xs font-semibold text-periwinkle-dark">{t.tagline}</span>
                  <span className="mt-1 block text-xs leading-relaxed text-navy/60">{t.oneLiner}</span>
                </button>
              );
            })}
          </div>

          <button onClick={startCheckout} data-testid="hero-continue-checkout"
            className="group mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-navy-dark active:scale-[0.99]">
            Continue to secure checkout — ${TIERS.find((t) => t.id === tier)?.price}
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
          </button>

          <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-crimson/15 bg-crimson-soft p-3.5">
            <BadgeDollarSign className="mt-0.5 h-5 w-5 shrink-0 text-crimson" />
            <p className="text-xs leading-relaxed text-navy/80">
              <span className="font-bold">Bust a fake, get rewarded.</span> Reporting an adulterated or fraudulent supplement can qualify for government whistleblower programs — your lab results could be the proof.
            </p>
          </div>

          <div className="mt-4 flex items-center gap-3 rounded-2xl border border-border p-3">
            <div className="hidden flex-1 sm:block"><DashboardPreview /></div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-navy">Track all your results</p>
              <p className="mt-1 text-xs text-navy/55">Create a free account to keep every report in one private dashboard. Optional — you can also just check out as a guest.</p>
              <button onClick={() => navigate("/register")} data-testid="hero-signup-link" className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-periwinkle-dark hover:text-navy">
                Create a free account <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Home() {
  const fade = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };
  const scrollToProcess = () => document.getElementById("process")?.scrollIntoView({ behavior: "smooth" });
  const [picked, setPicked] = useState(null);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-cream">
        <div className={`mx-auto grid max-w-7xl items-start gap-10 px-6 py-14 md:px-10 md:py-16 lg:gap-14 ${picked ? "max-w-3xl" : "lg:grid-cols-2"}`}>
          <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className={picked ? "w-full" : ""}>
            {!picked && <Eyebrow>100% Independent Lab Testing</Eyebrow>}
            <h1 className={`font-heading font-semibold leading-[1.06] tracking-tight text-navy ${picked ? "mt-0 text-2xl sm:text-3xl" : "mt-5 text-4xl sm:text-5xl lg:text-[52px]"}`} data-testid="hero-headline">
              {picked ? <>Let&rsquo;s verify your <span className="font-serif font-normal italic text-periwinkle-dark">{picked.label}</span>.</>
                      : <>Are Your Supplements <span className="font-serif font-normal italic text-navy/90">What They Claim To Be?</span></>}
            </h1>
            {!picked && (
              <p className="mt-4 max-w-xl text-lg leading-relaxed text-navy/65" data-testid="hero-subheadline">
                Independent lab testing for testosterone boosters, GLP-1 supplements &amp; more.
              </p>
            )}
            <div className="mt-7">
              <CategorySelector selected={picked} setSelected={setPicked} />
            </div>
            {!picked && (
              <button onClick={scrollToProcess} data-testid="hero-see-process"
                className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-navy/70 transition-colors hover:text-navy">
                Order here — see what happens next <ArrowDown className="h-4 w-4 animate-bounce" />
              </button>
            )}
          </motion.div>

          {/* Right: real news + actual FDA-flagged fakes (hidden once a category is picked) */}
          {!picked && (
          <div className="relative hidden lg:block" data-testid="hero-panel">
            <div className="mb-3 grid grid-cols-2 gap-4">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-navy/70">
                <Newspaper className="h-3.5 w-3.5 text-periwinkle-dark" /> Real news stories
              </span>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-red-600">
                <AlertTriangle className="h-3.5 w-3.5" /> Actual FDA-flagged fakes
              </span>
            </div>
            <div className="relative h-[600px] overflow-hidden">
              <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-16 bg-gradient-to-b from-cream to-transparent" />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-16 bg-gradient-to-t from-cream to-transparent" />
              <div className="grid grid-cols-2 gap-4">
                <div className="animate-vscroll flex flex-col gap-4">{[...NEWS, ...NEWS].map((n, i) => <NewsCard key={`n${i}`} {...n} />)}</div>
                <div className="animate-vscroll-rev flex flex-col gap-4 pt-8">{[...FDA_PRODUCTS, ...FDA_PRODUCTS].map((f, i) => <FdaTile key={`f${i}`} {...f} />)}</div>
              </div>
            </div>
          </div>
          )}
        </div>
      </section>

      {/* PRICING */}
      <Section className="bg-white" testid="home-pricing">
        <SectionHeading center eyebrow="Simple, transparent pricing"
          title={<>Two tests. <span className="font-serif font-normal italic">Total clarity.</span></>}
          subtitle="Free prepaid shipping included. Add more of the same test for just $50 each — up to 20 per order." />
        <div className="mx-auto mt-14 grid max-w-3xl gap-6 sm:grid-cols-2">
          {TIERS.map((t) => (
            <div key={t.id} className={`group relative flex flex-col rounded-3xl border bg-white p-8 transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-[0_22px_55px_rgba(47,76,120,0.12)] ${t.popular ? "border-navy ring-1 ring-navy" : "border-border"}`}>
              {t.popular && <span className="absolute -top-3 left-8 rounded-md bg-crimson px-3 py-1 text-xs font-bold text-white">Most Popular</span>}
              <h3 className="text-lg font-semibold text-navy">{t.name}</h3>
              <p className="mt-1 text-sm font-semibold text-periwinkle-dark">{t.tagline}</p>
              <p className="mt-4 font-heading text-5xl font-bold text-navy">${t.price}</p>
              <p className="mt-3 text-sm leading-relaxed text-navy/60">{t.oneLiner}</p>
              <ul className="mt-5 flex-1 space-y-2.5">
                {t.items.map((it) => (
                  <li key={it} className="flex items-start gap-2 text-sm text-navy/70">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-periwinkle-dark" /> {it}
                  </li>
                ))}
              </ul>
              <PillButton to={`/checkout?tier=${t.id}`} variant={t.popular ? "navy" : "outline"} className="mt-6 justify-center">Select {t.name}</PillButton>
            </div>
          ))}
        </div>
        {/* Plain-English difference */}
        <div className="mx-auto mt-8 max-w-3xl rounded-2xl border border-border bg-cream p-5 text-center text-sm leading-relaxed text-navy/70" data-testid="pricing-difference">
          <span className="font-semibold text-navy">The simple difference:</span> <span className="font-semibold text-navy">Label Accuracy</span> measures <em>one thing</em> — is the active ingredient really there at the dose claimed. <span className="font-semibold text-navy">Full Transparency</span> does the complete test for everything that <em>shouldn&rsquo;t</em> be there — allergens, heavy metals, fillers and hidden ingredients. If you have allergies or just want to be sure nothing else snuck in, choose Full Transparency.
        </div>
      </Section>

      {/* BUST A FAKE — you could be rewarded */}
      <section className="relative overflow-hidden bg-navy" data-testid="home-reward">
        <div className="lab-grid absolute inset-0 opacity-60" />
        <div className="relative mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-24">
          <div className="grid items-center gap-10 lg:grid-cols-[1.3fr_1fr]">
            <div>
              <Eyebrow dark>Whistleblower programs</Eyebrow>
              <h2 className="mt-5 font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Bust a fake — and you could <span className="font-serif font-normal italic text-sky-300">get rewarded</span>
              </h2>
              <p className="mt-5 max-w-xl leading-relaxed text-white/65">
                Reporting an adulterated or fraudulent supplement can qualify for federal and state whistleblower programs. An independent, lab-grade TestMySupps report is exactly the kind of documented evidence those programs rely on — so catching a fake could even pay you back.
              </p>
              <p className="mt-4 font-mono text-[11px] leading-relaxed text-white/40">
                Eligibility and any reward are determined solely by the relevant government program. We provide the analysis — not legal advice.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-white/10 bg-white/10">
              {[
                { icon: BadgeDollarSign, t: "Potential reward", d: "Whistleblower programs" },
                { icon: ShieldCheck, t: "Court-grade", d: "Documented lab evidence" },
                { icon: FileCheck2, t: "Your report", d: "Exactly the proof needed" },
                { icon: Building2, t: "Federal & state", d: "Multiple programs exist" },
              ].map((s) => (
                <div key={s.t} className="bg-navy p-6">
                  <s.icon className="h-6 w-6 text-sky-300" strokeWidth={1.6} />
                  <p className="mt-3 text-sm font-bold text-white">{s.t}</p>
                  <p className="mt-0.5 text-xs text-white/50">{s.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHAT HAPPENS NEXT */}
      <Section id="process" className="bg-white" testid="home-process">
        <SectionHeading center eyebrow="After you order"
          title={<>Here&rsquo;s <span className="font-serif font-normal italic">what happens next</span></>}
          subtitle="Clean, simple and fully tracked — from your door to our lab." />
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {NEXT_STEPS.map((s, i) => (
            <motion.div key={s.t} {...fade} transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group relative rounded-xl border border-border bg-white p-6 text-center transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1.5 hover:border-navy hover:shadow-[0_18px_45px_rgba(15,23,42,0.08)]">
              <span className="mx-auto grid h-14 w-14 place-items-center rounded-lg bg-secondary transition-colors duration-300 group-hover:bg-navy">
                <s.icon className="h-6 w-6 text-navy transition-colors duration-300 group-hover:text-white" />
              </span>
              <span className="mt-4 block font-mono text-xs font-semibold tracking-widest text-crimson">STEP 0{i + 1}</span>
              <h3 className="mt-1 text-base font-semibold text-navy">{s.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-navy/60">{s.d}</p>
            </motion.div>
          ))}
        </div>

        {/* Gentle process graphic */}
        <div className="mt-14 rounded-[2rem] border border-border bg-cream p-8 md:p-10">
          <div className="flex flex-col items-center gap-4 md:flex-row md:justify-between md:gap-2">
            {JOURNEY.map((j, i) => (
              <div key={j.label} className="flex w-full flex-col items-center md:flex-row md:gap-2">
                <motion.div initial={{ opacity: 0, scale: 0.85 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.12 }}
                  className="flex flex-col items-center gap-3 text-center">
                  <span className="grid h-28 w-28 place-items-center overflow-hidden rounded-3xl bg-white shadow-[0_10px_30px_rgba(47,76,120,0.1)] ring-1 ring-border">
                    <img src={j.img} alt={j.label} className="h-full w-full object-contain" loading="lazy" />
                  </span>
                  <span className="w-28 text-sm font-semibold text-navy">{j.label}</span>
                </motion.div>
                {i < JOURNEY.length - 1 && (
                  <div className="my-2 flex items-center justify-center md:my-0 md:flex-1">
                    <div className="h-10 w-px border-l-2 border-dashed border-periwinkle-dark/50 md:h-px md:w-full md:border-l-0 md:border-t-2" />
                    <ArrowRight className="ml-[-10px] hidden h-5 w-5 text-periwinkle-dark md:block" />
                    <ArrowDown className="mt-[-10px] h-5 w-5 text-periwinkle-dark md:hidden" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 flex justify-center">
          <PillButton to="/pricing" data-testid="process-cta">Start my order</PillButton>
        </div>
      </Section>

      {/* VALUE */}
      <Section testid="home-value">
        <SectionHeading center eyebrow="The Transparency Gap"
          title={<>What&rsquo;s on the label isn&rsquo;t always <span className="font-serif font-normal italic">what&rsquo;s in the bottle</span></>}
          subtitle="The supplement industry is lightly regulated. We give you objective, lab-measured facts — no marketing, no claims, just data." />
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {[
            { icon: Microscope, t: "Precise Analysis", d: "Lab-grade methods such as HPLC and LC-MS/MS measure exactly how much active ingredient is really present." },
            { icon: ShieldCheck, t: "Truly Independent", d: "We have zero brand affiliations. We never sell or recommend supplements." },
            { icon: FileCheck2, t: "Clear Reports", d: "Plain-English results comparing measured values against label claims." },
          ].map((c) => (
            <motion.div key={c.t} {...fade} className="group rounded-3xl border border-border bg-white p-8 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_22px_55px_rgba(47,76,120,0.12)]">
              <span className="grid h-12 w-12 place-items-center rounded-lg bg-secondary transition-colors duration-300 group-hover:bg-navy"><c.icon className="h-6 w-6 text-navy transition-colors duration-300 group-hover:text-white" /></span>
              <h3 className="mt-5 text-xl font-semibold text-navy">{c.t}</h3>
              <p className="mt-2 text-navy/60">{c.d}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* FEATURED QUOTE */}
      <Section className="bg-cream" testid="home-featured-quote">
        <div className="mx-auto max-w-4xl text-center">
          <Quote className="mx-auto h-10 w-10 text-periwinkle-dark" />
          <blockquote className="mt-6 font-serif text-2xl font-normal italic leading-snug text-navy sm:text-3xl lg:text-[34px]">
            &ldquo;{FEATURED_QUOTE.text}&rdquo;
          </blockquote>
          <div className="mt-6 flex items-center justify-center gap-1">{[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />)}</div>
          <p className="mt-4 text-sm font-semibold text-navy">{FEATURED_QUOTE.name}</p>
          <p className="text-xs text-navy/50">{FEATURED_QUOTE.role}</p>
        </div>
      </Section>

      {/* HOW WE TEST — white clinical bento */}
      <Section className="bg-white" testid="home-lab">
        <SectionHeading center eyebrow="Inside the lab"
          title={<>The instruments behind <span className="font-serif font-normal italic text-crimson">every report</span></>}
          subtitle="We're a professional analytical lab — using the same families of instruments other professional labs rely on. Here's what each one does, and when we reach for it." />
        <div className="mt-14 grid gap-6 lg:grid-cols-[1.05fr_1.2fr]">
          {/* Feature image panel */}
          <div className="relative min-h-[440px] overflow-hidden rounded-2xl border border-border">
            <img src="https://images.unsplash.com/photo-1614935151651-0bea6508db6b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Analytical instruments in the lab" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/55 to-navy/10" />
            <div className="absolute inset-x-0 bottom-0 p-7">
              <span className="inline-flex items-center gap-2 rounded-md bg-crimson px-3 py-1 font-mono text-[11px] font-semibold uppercase tracking-wider text-white">Professional analytical lab</span>
              <p className="mt-3 text-xl font-bold text-white">A working analytical lab — not a mailroom.</p>
              <div className="mt-5 grid grid-cols-3 gap-3 border-t border-white/15 pt-4">
                {[{ n: "6+", l: "Methods" }, { n: "ppb", l: "Detection limits" }, { n: "7-day", l: "Turnaround" }].map((s) => (
                  <div key={s.l}>
                    <p className="font-heading text-2xl font-black text-sky-300">{s.n}</p>
                    <p className="font-mono text-[10px] uppercase tracking-wider text-white/55">{s.l}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* Instrument bento with 1px separators (control-room) */}
          <div className="grid gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-2">
            {LAB_CAPABILITIES.map((c) => (
              <div key={c.name} className="group bg-white p-6 transition-colors duration-200 hover:bg-secondary">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-mono text-sm font-bold tracking-tight text-navy">{c.name}</span>
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-secondary text-crimson transition-colors duration-200 group-hover:bg-crimson group-hover:text-white">
                    <FlaskConical className="h-4 w-4" />
                  </span>
                </div>
                <p className="mt-1 font-mono text-[10px] font-medium uppercase tracking-wider text-crimson">{c.full}</p>
                <p className="mt-2 text-xs leading-relaxed text-navy/60">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
        <p className="mx-auto mt-10 max-w-2xl text-center font-mono text-[11px] leading-relaxed text-navy/45">
          We select the method best suited to each supplement. We report measured values only and make no health claims — we are a testing service, not a medical provider.
        </p>
      </Section>

      {/* TESTIMONIALS */}
      <Section className="bg-cream" testid="home-testimonials">
        <SectionHeading center eyebrow="Trusted by customers"
          title={<>Real people, <span className="font-serif font-normal italic text-periwinkle-dark">real discoveries</span></>} />
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} className="rounded-3xl border border-border bg-white p-8 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_18px_45px_rgba(47,76,120,0.1)]">
              <Quote className="h-8 w-8 text-periwinkle-dark" />
              <div className="mt-4 flex gap-1">{[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />)}</div>
              <p className="mt-4 text-navy/75">{t.text}</p>
              <p className="mt-5 text-sm font-semibold text-navy">{t.name}</p>
              <p className="text-xs text-navy/50">{t.role}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* B2B — dark section with university-adjacent imagery */}
      <section className="relative overflow-hidden bg-navy" data-testid="home-b2b">
        <div className="lab-grid absolute inset-0 opacity-50" />
        <div className="relative mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-24">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <Eyebrow dark>For brands & retailers</Eyebrow>
              <h2 className="mt-5 font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Verify your <span className="font-serif font-normal italic text-sky-300">entire product line</span>
              </h2>
              <p className="mt-5 max-w-xl leading-relaxed text-white/65">
                Brands, retailers and distributors trust independent verification to protect their customers and their reputation. We run batch testing across your full catalog and deliver clean, documented reports your team can stand behind.
              </p>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <CheckItem light>Full-catalog batch testing</CheckItem>
                <CheckItem light>Certificates of Analysis (COAs)</CheckItem>
                <CheckItem light>White-label / co-branded reports</CheckItem>
                <CheckItem light>Supply-chain & incoming-lot audits</CheckItem>
              </div>
              <div className="mt-7 overflow-hidden rounded-xl border border-white/10">
                <img src="https://images.unsplash.com/photo-1448584109583-8f5fe2e61544?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                  alt="University science building" className="h-40 w-full object-cover" loading="lazy" />
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white p-7 shadow-[0_30px_70px_rgba(0,0,0,0.35)]">
              <h3 className="font-heading text-xl font-bold text-navy">Contact us for pricing</h3>
              <p className="mt-1.5 text-sm text-navy/60">Tell us about your line and we'll send a tailored B2B quote — typically within one business day.</p>
              <div className="mt-5"><InquiryForm type="b2b" cta="Request B2B pricing" /></div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT — message the science team */}
      <Section className="bg-white" testid="home-contact">
        <div className="grid items-start gap-12 lg:grid-cols-2">
          <div>
            <Eyebrow>Talk to a scientist</Eyebrow>
            <h2 className="mt-5 font-heading text-3xl font-bold tracking-tight text-navy sm:text-4xl">
              Questions? <span className="font-serif font-normal italic text-crimson">Message our science team</span>
            </h2>
            <p className="mt-5 max-w-xl leading-relaxed text-navy/60">
              Not sure which test you need, or have a question about methods, detection limits or what we can identify? Send us a note — a member of our analytical team will get back to you.
            </p>
            <div className="mt-7 overflow-hidden rounded-2xl border border-border">
              <img src="https://images.unsplash.com/photo-1511174511562-5f7f18b874f8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Researcher at a microscope" className="h-48 w-full object-cover" loading="lazy" />
            </div>
            <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3">
              <span className="inline-flex items-center gap-2 text-sm text-navy/70"><Mail className="h-4 w-4 text-crimson" /> team@testmysupps.co</span>
              <span className="inline-flex items-center gap-2 text-sm text-navy/70"><MessageSquare className="h-4 w-4 text-crimson" /> Replies within 1 business day</span>
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-secondary p-7">
            <h3 className="font-heading text-xl font-bold text-navy">Send a message</h3>
            <p className="mt-1.5 text-sm text-navy/60">We read every message. No bots, no sales pressure.</p>
            <div className="mt-5"><InquiryForm type="contact" cta="Send to science team" /></div>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section testid="home-cta">
        <div className="overflow-hidden rounded-[2rem] bg-beige p-10 text-center md:p-16">
          <h2 className="mx-auto max-w-2xl font-heading text-3xl font-semibold tracking-tight text-navy sm:text-4xl lg:text-5xl">
            Find out what&rsquo;s <span className="font-serif font-normal italic">really</span> in your supplements
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-navy/65 flex items-center justify-center gap-2">
            <Lock className="h-4 w-4 text-navy/50" /> Secure checkout · free shipping · money-back guarantee within 14 days.
          </p>
          <div className="mt-8 flex justify-center">
            <PillButton to="/pricing" data-testid="cta-bottom-button">Get Started</PillButton>
          </div>
        </div>
      </Section>
    </div>
  );
}
